/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import type TimeAgo from "javascript-time-ago";
import type { FormatStyleName } from "javascript-time-ago";

import { CosmosEnumTimeAndDateFormat, CosmosEnumTimeZone } from "@cosmos/hygraph-schema";

export const dateToTimeAgoShort = (date: string, timeAgo: TimeAgo): string => {
	try {
		const formatted = timeAgo.format(new Date(date), "short" as FormatStyleName);
		return formatted;
	} catch (e) {
		console.error(e);
		return "";
	}
};

export const enumTimeZoneToIANA = (enumValue?: string | null): string => {
	if (!enumValue) return "";
	if (enumValue.startsWith("Etc_GMT_plus")) {
		return enumValue.replace("Etc_GMT_plus_", "Etc/GMT+");
	}
	// replace only the first underscore with a slash
	const [region, ...rest] = enumValue.split("_");
	return `${region}/${rest.join("_")}`;
};

export const formatTimeAndDate = (
	timeAndDate: string,
	locale = "en-US",
	timeAndDateFormat: CosmosEnumTimeAndDateFormat | null = CosmosEnumTimeAndDateFormat.MonthDayYear,
	timeZone?: CosmosEnumTimeZone | null,
): string => {
	// split date to avoid timezone adjustments by Date constructor
	const [dayPart, timePart] = timeAndDate.split("T");
	const [y, m, d] = dayPart.split("-").map((i) => parseInt(i, 10));
	const [hh, mm] = timePart ? [parseInt(timePart.slice(0, 2), 10), parseInt(timePart.slice(3, 5), 10)] : [0, 0];

	let dateObj: Date;
	if (
		timeAndDateFormat === CosmosEnumTimeAndDateFormat.WeekdayMonthDayTimeTimeZone ||
		timeAndDateFormat === CosmosEnumTimeAndDateFormat.WeekdayMonthDayYearTimeTimeZone
	) {
		dateObj = new Date(Date.UTC(y, m - 1, d, hh, mm)); // month is 0-indexed
	} else {
		dateObj = new Date(y, m - 1, d, hh, mm); // month is 0-indexed
	}

	if (isNaN(dateObj.getTime())) throw new Error("Invalid date"); // ensure successful conversion

	switch (timeAndDateFormat) {
		case CosmosEnumTimeAndDateFormat.WeekdayMonthDayTimeTimeZone:
			return dateObj.toLocaleDateString(locale, {
				weekday: "long",
				month: "short",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				timeZone: enumTimeZoneToIANA(timeZone) || undefined,
				timeZoneName: "short",
			});
		case CosmosEnumTimeAndDateFormat.WeekdayMonthDayYearTimeTimeZone:
			return dateObj.toLocaleDateString(locale, {
				weekday: "long",
				month: "short",
				day: "numeric",
				year: "numeric",
				hour: "numeric",
				minute: "numeric",
				timeZone: enumTimeZoneToIANA(timeZone) || undefined,
				timeZoneName: "short",
			});
		case CosmosEnumTimeAndDateFormat.MonthDay:
			return dateObj.toLocaleDateString(locale, { month: "long", day: "numeric" });
		default:
			return dateObj.toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" });
	}
};

export const hygraphDateToLocaleDateString = (
	hygraphDate?: string | null,
	hygraphEndDate?: string | null,
	locale?: string,
	timeAndDateFormat?: CosmosEnumTimeAndDateFormat | null,
	timeZone?: CosmosEnumTimeZone | null,
): string => {
	if (!hygraphDate) return "";
	const formattedStart = formatTimeAndDate(hygraphDate, locale, timeAndDateFormat, timeZone);
	if (hygraphEndDate) {
		const formattedEnd = formatTimeAndDate(hygraphEndDate, locale, timeAndDateFormat, timeZone);
		return `${formattedStart} - ${formattedEnd}`;
	}
	return formattedStart;
};

/**
 * Safely converts a time/date value to a timestamp number.
 * Handles string, number, Date object, and invalid/null/undefined values.
 * @param timeAndDate - The time/date value to convert (string, number, Date, or unknown)
 * @returns The timestamp in milliseconds, or 0 if the value cannot be converted
 */
export const safeGetTime = (timeAndDate: unknown): number => {
	if (!timeAndDate) return 0;
	if (typeof timeAndDate === "string" || typeof timeAndDate === "number") {
		return new Date(timeAndDate).getTime();
	}
	if (timeAndDate instanceof Date) {
		return timeAndDate.getTime();
	}
	return 0;
};
